import {
  ICON_GRID,
  IconSizeName,
  iconSize,
  iconStroke,
  opticalScale,
} from './icons'

// ----------------------------------------------------------------------
// Icon geometry: from a requested size to the pixels the glyph occupies and
// the stroke-width units that make its rendered weight equal the token.
//
// Both icon packs draw on a 24 unit grid with a 2 unit stroke, and an SVG
// stroke scales with the box it is drawn in. Left alone, a 14px glyph puts
// down 1.17 device pixels and a 24px glyph puts down 2, so a chip mark reads
// faint beside a page action drawn from the same family. The tokens name the
// rendered weight each optical size should have; this module turns that into
// the unit value the wrapper writes onto the svg, so the weight on screen is
// the one in the table rather than a by-product of scaling.
//
// This is pure arithmetic over the token module, kept out of the component so
// a test can hold every number without rendering anything.
// ----------------------------------------------------------------------

/**
 * The optical steps in ascending order. Ascending matters: when a requested
 * size sits exactly between two steps, the smaller one is kept, because a
 * glyph one step too small sits inside its control while one step too large
 * touches its edges.
 */
const STEPS_ASCENDING = (Object.keys(iconSize) as IconSizeName[]).sort(
  (a, b) => iconSize[a] - iconSize[b],
)

/**
 * The reference weight: the step whose stroke every other one is tuned
 * against, and the wrapper's default when a call site names no size. It is
 * also the stroke reported when the glyph's size is unknown, because that is
 * the weight it would take the moment a size is given.
 */
export const DEFAULT_ICON_SIZE: IconSizeName = 'toolbar'

/**
 * Stroke units are written to three decimals. A browser does not resolve a
 * stroke below a thousandth of a unit, and the raw quotient is often a
 * repeating decimal (1.35 times 24 over 14) or carries floating point noise
 * (1.3 times 24 over 16 comes out as 1.9500000000000002), either of which
 * would make the emitted CSS unreadable and the tests brittle.
 */
const STROKE_PRECISION = 1000

/**
 * The optical step nearest to a pixel size. Ties go down (15 is `chip`, 22 is
 * `toolbar`). Anything above the largest step snaps to it: there is no
 * illustration tier among icons, so a 30px glyph is an `action` drawn large.
 */
export const snapToIconGrid = (px: number): IconSizeName =>
  STEPS_ASCENDING.reduce((best, step) =>
    Math.abs(iconSize[step] - px) < Math.abs(iconSize[best] - px) ? step : best,
  )

/** Stroke-width in svg user units that renders `strokeDevicePx` at `px`. */
export const strokeUnitsFor = (strokeDevicePx: number, px: number): number =>
  Math.round(((strokeDevicePx * ICON_GRID) / px) * STROKE_PRECISION) /
  STROKE_PRECISION

export interface IconGeometryInput {
  /** An optical step by name, or a pixel size to snap onto the grid. */
  size?: IconSizeName | number
  /**
   * Legacy sizing. A number is honoured as drawn and takes the stroke of the
   * nearest step; a string (`auto`, `1em`) is a size this module cannot read.
   */
  width?: number | string
  height?: number | string
  /**
   * The glyph is the only content of its control, so it carries all of the
   * control's meaning and takes the heavier solo weight.
   */
  solo?: boolean
  /**
   * The glyph name, which is what carries an optical correction. Omitted, the
   * geometry is the uncorrected one — the same answer as for the glyphs that
   * have no correction, which is almost all of them.
   */
  glyph?: string
}

export interface IconGeometry {
  /** The pixel size the glyph is drawn at, or undefined when it follows text. */
  px: number | undefined
  /** The rendered stroke the glyph should put down, in device pixels. */
  strokeDevicePx: number
  /**
   * The `stroke-width` to write onto the svg so it renders `strokeDevicePx`
   * at `px`. Undefined when the size is unknown, in which case no rule is
   * emitted and the pack's own stroke stands.
   *
   * Already divided by `opticalScale`, because the correction is applied as a
   * transform on the drawn glyph and a transform scales the stroke with it.
   * The contract this field states is the rendered weight, so the compensation
   * belongs to the same arithmetic rather than to the component.
   */
  strokeUnits: number | undefined
  /**
   * The fraction of `px` the mark itself is drawn at. `1` unless the glyph has
   * a recorded optical correction, and `1` whenever the size is unknown: the
   * correction is only applied where the wrapper also owns the weight, because
   * shrinking a mark whose stroke this module cannot state would quietly take
   * that stroke down with it.
   */
  opticalScale: number
  /** The optical step the geometry was taken from, when there is one. */
  sizeName: IconSizeName | undefined
}

/**
 * Resolve what a glyph should measure and how heavy its stroke should be.
 *
 * Precedence is `size`, then a numeric `width` or `height`, then nothing: a
 * glyph with no known size is one that follows the surrounding font size, and
 * a stroke cannot be tuned for a size that is only known at layout time.
 */
export const resolveIconGeometry = ({
  size,
  width,
  height,
  solo = false,
  glyph,
}: IconGeometryInput): IconGeometry => {
  let px: number | undefined
  let sizeName: IconSizeName | undefined

  if (typeof size === 'string') {
    sizeName = size
    px = iconSize[size]
  } else if (typeof size === 'number') {
    sizeName = snapToIconGrid(size)
    px = iconSize[sizeName]
  } else {
    const legacy = [width, height].find(
      (value): value is number => typeof value === 'number',
    )
    if (legacy !== undefined) {
      px = legacy
      sizeName = snapToIconGrid(legacy)
    }
  }

  const strokeDevicePx = solo
    ? iconStroke.solo
    : iconStroke[sizeName ?? DEFAULT_ICON_SIZE]

  // The correction rides with the size. A glyph whose size this module cannot
  // state is one whose stroke it does not state either, and a transform put on
  // it would take the pack's own stroke down by the same fraction with nothing
  // left to restore it.
  const drawn = px !== undefined && px > 0 ? px : undefined
  const scale = drawn === undefined ? 1 : opticalScale(glyph)

  return {
    px,
    strokeDevicePx,
    strokeUnits:
      drawn === undefined
        ? undefined
        : Math.round(
            (strokeUnitsFor(strokeDevicePx, drawn) / scale) * STROKE_PRECISION,
          ) / STROKE_PRECISION,
    opticalScale: scale,
    sizeName,
  }
}
